import { Function, Token, } from "@siteimprove/alfa-css";
import { Option } from "@siteimprove/alfa-option";
import { Parser } from "@siteimprove/alfa-parser";
import { Context } from "../../../context.js";
import { Specificity } from "../../../specificity.js";
import { PseudoClassSelector } from "./pseudo-class.js";
const { either, map, right } = Parser;
const { parseColon } = Token;
/**
 * {@link https://drafts.csswg.org/css-scoping-1/#selectordef-host}
 *
 * @privateRemarks
 * Since WithSelector has a mandatory selector, it is not convenient
 * to use it to group both the functional and non-functional variants.
 *
 * In CSS lingo, `:host` only accepts a compound selector, but simple
 * selectors are also compounds. Alfa does make a type difference between
 * a Simple selector and a compound selector with only a single Simple
 * selector. Hence, this also accepts Simple selector. This also accepts
 * pseudo-elements, which seems to not be really allowed by the specification.
 *
 * @public
 */
export class Host extends PseudoClassSelector {
    static of(selector) {
        return new Host(Option.from(selector));
    }
    _selector;
    constructor(selector) {
        super("host", Specificity.sum(selector
            .map((selector) => selector.specificity)
            .getOr(Specificity.empty()), Specificity.of(0, 1, 0)));
        this._selector = selector;
    }
    get selector() {
        return this._selector;
    }
    /**
     * @remarks
     * `:host` never matches anything in its own tree.
     */
    matches() {
        return false;
    }
    /**
     * Checks whether a shadow host matches.
     *
     * @remarks
     * This must be called with `element` being the shadow host of
     * the Document that defines the selector.
     */
    matchHost(element, context = Context.empty()) {
        return this._selector.every((selector) => selector.matches(element, context));
    }
    *[Symbol.iterator]() {
        yield this;
    }
    equals(value) {
        return value instanceof Host && value._selector.equals(this._selector);
    }
    toJSON() {
        return {
            ...super.toJSON(),
            ...(this._selector.isSome()
                ? { selector: this._selector.get().toJSON() }
                : {}),
        };
    }
}
/**
 * @public
 */
(function (Host) {
    function isHost(value) {
        return value instanceof Host;
    }
    Host.isHost = isHost;
    Host.parse = (parseSelector) => either(
    // We need to try the functional variant first to avoid the non-functional
    // greedily passing.
    map(right(parseColon, Function.parse("host", parseSelector)), ([, selector]) => Host.of(selector)), PseudoClassSelector.parseNonFunctional("host", Host.of));
})(Host || (Host = {}));
//# sourceMappingURL=host.js.map